import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { ApiError } from '../api.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { navigate } from '../router.js';
import { Avatar, Callout, Dialog, ErrorBox, Loading, MoreOptions, Pill, toast } from '../ui.js';
import { CrewSend } from './CrewSend.js';
import { SendForReview } from './sendForReview.js';
import { VersionMenu } from './withdrawReview.js';
import { Tip, TIPS } from '../guide.js';
import { PageTour, playTour, TOUR_TEXT } from '../tour.js';

/**
 * Review in the Hub (owner decision 2026-10-17: Ziflow ends 7 Nov). One version of a video: the film played straight
 * from Drive, comments on an exact frame or a range with a point or a drawing in the
 * picture, the previous version's comments, a second version side by side, and the decision for the stage it is in.
 * The same player serves staff (internal comments, CD and producer decisions, Approve and send to client) and the
 * client in the portal (comment, Approve, Approve with small adjustments, Request changes).
 */

export interface FileView {
  key: string;
  name: string;
  format: string | null;
  kind: 'video' | 'image' | 'other';
  url: string;
  /** null: no download here (the client gets final formats only) */
  downloadUrl: string | null;
}
export type Drawing = { points: [number, number][] }[];
export interface CommentView {
  id: string;
  parentId: string | null;
  fileKey: string;
  author: string;
  staff: boolean;
  mine: boolean;
  frame: number | null;
  endFrame: number | null;
  fps: number | null;
  pin: { x: number; y: number } | null;
  drawing?: Drawing | null;
  body: string;
  internal: boolean;
  done: boolean;
  createdAt: string;
  edited: boolean;
}
export interface NewComment {
  body: string;
  frame: number | null;
  endFrame: number | null;
  fps: number;
  pin: { x: number; y: number } | null;
  drawing: Drawing | null;
  internal: boolean;
  parentId: string | null;
  fileKey: string;
}
interface Previous {
  id: string;
  number: number;
  round: number | null;
  comments: { id: string; frame: number | null; fps: number | null; author: string; body: string; internal: boolean; done: boolean; staff: boolean }[];
}

/** Review exports are constant 25 fps (the review export template). */
export const FPS = 25;

/** Frame → "00:12:07" (minutes, seconds, frames), with hours when the film is that long. */
export function timecode(frame: number, fps = FPS): string {
  const f = Math.max(0, Math.round(frame));
  const r = Math.round(fps);
  const ff = f % r;
  const s = Math.floor(f / r);
  const two = (n: number) => String(n).padStart(2, '0');
  const hh = Math.floor(s / 3600);
  return `${hh ? `${two(hh)}:` : ''}${two(Math.floor(s / 60) % 60)}:${two(s % 60)}:${two(ff)}`;
}
/** Frame → "0:12" (minutes and seconds) for the client, who does not count frames. */
export function clock(frame: number, fps = FPS): string {
  const s = Math.floor(Math.max(0, Math.round(frame)) / Math.round(fps));
  const two = (n: number) => String(n).padStart(2, '0');
  const h = Math.floor(s / 3600);
  return h ? `${h}:${two(Math.floor(s / 60) % 60)}:${two(s % 60)}` : `${Math.floor(s / 60)}:${two(s % 60)}`;
}
/** The frame shown at a media time (a small epsilon so 0.04 s at 25 fps is frame 1, not 0). */
export const frameAt = (t: number, fps = FPS) => Math.floor(t * fps + 1e-3);
/** The media time to seek to for a frame: its middle, so the browser lands on it and not the one before. */
export const timeOf = (frame: number, fps = FPS) => (frame + 0.5) / fps;
/** An SVG path for a stroke in a 0–100 box. */
export const strokePath = (pts: [number, number][]) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${(x * 100).toFixed(2)} ${(y * 100).toFixed(2)}`).join(' ');

const errText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

const T = texts(
  {
    play: 'Play',
    pause: 'Pause',
    back: 'One frame back',
    fwd: 'One frame forward',
    clickHint: 'Click in the picture where you want a change',
    general: 'Comment on the whole film',
    at: (tc: string) => `At ${tc}`,
    range: (a: string, b: string) => `${a} – ${b}`,
    write: 'Write your comment…',
    send: 'Comment',
    cancel: 'Cancel',
    internal: 'Internal – the client does not see it',
    internalTag: 'Internal',
    reply: 'Reply',
    done: 'Done',
    undone: 'Not done',
    remove: 'Remove',
    edited: 'edited',
    noComments: 'No comments yet.',
    comments: 'Comments',
    noPreview: 'This file cannot be shown in the browser.',
    showDone: (n: number) => `Show ${n} done`,
    hideDone: 'Hide done',
    draw: 'Draw',
    drawing: 'Drawing – drag in the picture',
    clearDrawing: 'Clear drawing',
    endHere: (tc: string) => `Until ${tc}`,
    noEnd: 'One frame',
    download: 'Download',
    cannotPlay: 'The browser cannot play this file. Export an MP4 (H.264) and send that for review.',
    askKff: 'The film cannot be shown here. Please get in touch with us.',
    previous: (n: number) => `From version ${n}`,
    fixed: (a: number, b: number) => `${a} of ${b} done`,
    compare: 'Compare with',
    noCompare: 'No comparison',
    thisOne: 'This version',
    other: (n: number) => `Version ${n}`,
    full: 'Full screen',
    exitFull: 'Leave full screen',
    loading: 'Loading the film…',
    start: 'Play the film',
    sayAt: 'Click in the picture, or write a comment here…',
    open: (n: number) => `Open ${n}`,
    all: (n: number) => `All ${n}`,
    allDone: 'Everything is done.',
    dark: 'Dark',
    light: 'Light',
    themeLabel: 'Dark or light review'
  },
  {
    play: 'Spela upp',
    pause: 'Pausa',
    back: 'En bildruta bakåt',
    fwd: 'En bildruta framåt',
    clickHint: 'Klicka i bilden där du vill ha en ändring',
    general: 'Kommentera hela filmen',
    at: (tc: string) => `Vid ${tc}`,
    range: (a: string, b: string) => `${a} – ${b}`,
    write: 'Skriv din kommentar…',
    send: 'Kommentera',
    cancel: 'Avbryt',
    internal: 'Intern – kunden ser den inte',
    internalTag: 'Intern',
    reply: 'Svara',
    done: 'Klar',
    undone: 'Inte klar',
    remove: 'Ta bort',
    edited: 'ändrad',
    noComments: 'Inga kommentarer ännu.',
    comments: 'Kommentarer',
    noPreview: 'Filen kan inte visas i webbläsaren.',
    showDone: (n: number) => `Visa ${n} klara`,
    hideDone: 'Dölj klara',
    draw: 'Rita',
    drawing: 'Ritar – dra i bilden',
    clearDrawing: 'Sudda ritningen',
    endHere: (tc: string) => `Till ${tc}`,
    noEnd: 'En bildruta',
    download: 'Ladda ner',
    cannotPlay: 'Webbläsaren kan inte spela den här filen. Exportera en MP4 (H.264) och skicka den för granskning.',
    askKff: 'Filmen kan inte visas här. Hör av dig till oss.',
    previous: (n: number) => `Från version ${n}`,
    fixed: (a: number, b: number) => `${a} av ${b} klara`,
    compare: 'Jämför med',
    noCompare: 'Ingen jämförelse',
    thisOne: 'Den här versionen',
    other: (n: number) => `Version ${n}`,
    full: 'Helskärm',
    exitFull: 'Lämna helskärm',
    loading: 'Laddar filmen…',
    start: 'Spela filmen',
    sayAt: 'Klicka i bilden, eller skriv en kommentar här…',
    open: (n: number) => `Öppna ${n}`,
    all: (n: number) => `Alla ${n}`,
    allDone: 'Allt är klart.',
    dark: 'Mörkt',
    light: 'Ljust',
    themeLabel: 'Mörk eller ljus granskning'
  }
);

interface PlayerProps {
  lang: Lang;
  files: FileView[];
  comments: CommentView[];
  /** staff: internal comments and ticking done */
  staff: boolean;
  /** commenting is open (the version waits for this side) */
  canComment: boolean;
  add: (c: NewComment) => Promise<void>;
  /** staff only: tick done */
  done?: (id: string, done: boolean) => Promise<void>;
  remove?: (id: string) => Promise<void>;
  /** files of another version, played side by side in step */
  compare?: { label: string; files: FileView[] } | null;
  thisLabel?: string;
  /** the previous version's comments (what was asked, and what was fixed) */
  previous?: Previous | null;
  /** the one thing to do now (design B): the decision card, above the comments */
  next?: ComponentChildren;
}

type Draft = { frame: number | null; endFrame: number | null; pin: { x: number; y: number } | null; drawing: Drawing; parentId: string | null };

/** The film with its comments: click in the picture to comment on that frame, click a comment to go back to it. */
export function ReviewPlayer(p: PlayerProps) {
  const t = T[p.lang];
  // staff count frames; the client sees minutes and seconds
  const tc = (f: number, fps = FPS) => (p.staff ? timecode(f, fps) : clock(f, fps));
  const [full, setFull] = useState(false);
  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === screen.current);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  /** the picture with its pins and drawings fills the screen; an iPhone has no element full screen, so its own player opens */
  const fullscreen = () => {
    const el = screen.current;
    const v = video.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (document.fullscreenElement) void document.exitFullscreen();
    else if (el?.requestFullscreen) void el.requestFullscreen().catch(() => v?.webkitEnterFullscreen?.());
    else v?.webkitEnterFullscreen?.();
  };
  const [fileKey, setFileKey] = useState(p.files[0]?.key ?? '');
  const file = p.files.find((f) => f.key === fileKey) ?? p.files[0];
  const twin = p.compare?.files.find((f) => f.key === file?.key) ?? p.compare?.files.find((f) => f.format && f.format === file?.format) ?? p.compare?.files[0] ?? null;
  const video = useRef<HTMLVideoElement>(null);
  const other = useRef<HTMLVideoElement>(null);
  const screen = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [frame, setFrame] = useState(0);
  const [total, setTotal] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [drawMode, setDrawMode] = useState(false);
  const [stroke, setStroke] = useState<[number, number][] | null>(null);
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [broken, setBroken] = useState<string | null>(null);
  // the film streams from Drive: a spinner until the first picture is there, and while it buffers
  const [loading, setLoading] = useState(true);
  // until the film has played once, a client's click in the picture plays it (a new viewer expects that) instead of
  // starting a comment; staff comment at once
  const [started, setStarted] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);

  // follow the playhead frame by frame while playing (requestVideoFrameCallback where the browser has it)
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    let raf = 0;
    let vfc = 0;
    const rvfc = (v as HTMLVideoElement & { requestVideoFrameCallback?: (cb: (now: number, m: { mediaTime: number }) => void) => number }).requestVideoFrameCallback?.bind(v);
    const tick = () => {
      setFrame(frameAt(v.currentTime));
      if (rvfc) vfc = rvfc((_n, m) => (setFrame(frameAt(m.mediaTime)), tick()));
      else raf = requestAnimationFrame(tick);
    };
    const meta = () => setTotal(frameAt(v.duration || 0));
    const sync = () => setFrame(frameAt(v.currentTime));
    const ready = () => setLoading(false);
    const wait = () => setLoading(true);
    const on = () => {
      setPlaying(true);
      setStarted(true);
      const o = other.current;
      if (!o) return;
      o.currentTime = v.currentTime;
      void o.play().catch(() => {});
    };
    const off = () => {
      setPlaying(false);
      sync();
      const o = other.current;
      if (!o) return;
      o.pause();
      o.currentTime = v.currentTime;
    };
    const bad = () => (setBroken(file?.key ?? ''), setLoading(false));
    if (v.readyState >= 2) setLoading(false);
    v.addEventListener('loadeddata', ready);
    v.addEventListener('canplay', ready);
    v.addEventListener('playing', ready);
    v.addEventListener('waiting', wait);
    v.addEventListener('loadedmetadata', meta);
    v.addEventListener('seeked', sync);
    v.addEventListener('play', on);
    v.addEventListener('pause', off);
    v.addEventListener('ended', off);
    v.addEventListener('error', bad);
    tick();
    return () => {
      cancelAnimationFrame(raf);
      (v as HTMLVideoElement & { cancelVideoFrameCallback?: (h: number) => void }).cancelVideoFrameCallback?.(vfc);
      v.removeEventListener('loadeddata', ready);
      v.removeEventListener('canplay', ready);
      v.removeEventListener('playing', ready);
      v.removeEventListener('waiting', wait);
      v.removeEventListener('loadedmetadata', meta);
      v.removeEventListener('seeked', sync);
      v.removeEventListener('play', on);
      v.removeEventListener('pause', off);
      v.removeEventListener('ended', off);
      v.removeEventListener('error', bad);
    };
  }, [file?.url]);
  useEffect(() => {
    setBroken(null);
    setLoading(true);
    setStarted(false);
  }, [file?.url]);

  const seek = (f: number) => {
    const v = video.current;
    if (!v) return;
    v.pause();
    v.currentTime = Math.min(Math.max(0, timeOf(f)), v.duration || Infinity);
    if (other.current) other.current.currentTime = v.currentTime;
    setFrame(f);
  };
  const toggle = () => {
    const v = video.current;
    if (!v) return;
    if (v.paused) {
      setDraft(null);
      setDrawMode(false);
      void v.play();
    } else v.pause();
  };
  const start = (d: Omit<Draft, 'drawing' | 'endFrame'>) => {
    setDraft({ ...d, endFrame: null, drawing: [] });
    setInternal(false);
    setDrawMode(false);
    // focus without scrolling (owner 2026-11-03): the page must not jump away from the picture the comment is about
    setTimeout(() => box.current?.focus({ preventScroll: true }), 0);
  };
  const point = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = screen.current!.getBoundingClientRect();
    const at = (v: number, from: number, size: number) => (size > 0 ? Math.min(1, Math.max(0, (v - from) / size)) : 0.5);
    return [at(e.clientX, r.left, r.width), at(e.clientY, r.top, r.height)];
  };
  /** a click in the picture: pause there and comment on that frame and point */
  const pick = (e: MouseEvent) => {
    if (drawMode) return;
    if (!p.canComment || (file?.kind === 'video' && !p.staff && !started)) return toggle();
    const [x, y] = point(e);
    const v = video.current;
    if (v && !v.paused) v.pause();
    start({ frame: file?.kind === 'video' ? frameAt(v?.currentTime ?? 0) : null, pin: { x, y }, parentId: null });
  };
  // drawing: drag in the picture while Draw is on
  const down = (e: PointerEvent) => {
    if (!drawMode || !draft) return;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setStroke([point(e)]);
  };
  const move = (e: PointerEvent) => {
    if (!stroke || stroke.length >= 500) return;
    setStroke([...stroke, point(e)]);
  };
  const up = () => {
    if (stroke && stroke.length > 1 && draft) setDraft({ ...draft, drawing: [...draft.drawing, { points: stroke }].slice(0, 20) });
    setStroke(null);
  };
  const submit = async (e: Event) => {
    e.preventDefault();
    if (!draft || !text.trim() || busy) return;
    setBusy(true);
    try {
      await p.add({
        body: text.trim(),
        frame: draft.frame,
        endFrame: draft.endFrame,
        fps: FPS,
        pin: draft.pin,
        drawing: draft.drawing.length ? draft.drawing : null,
        internal: p.staff && internal,
        parentId: draft.parentId,
        fileKey: file?.key ?? ''
      });
      setText('');
      setDraft(null);
      setDrawMode(false);
    } catch (err) {
      toast({ text: errText(err) });
    } finally {
      setBusy(false);
    }
  };
  // keyboard: space plays, arrows step a frame (not while typing)
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT' || file?.kind !== 'video') return;
      const go = e.key === ' ' ? toggle : e.key === 'ArrowLeft' ? () => seek(Math.max(0, frame - 1)) : e.key === 'ArrowRight' ? () => seek(frame + 1) : null;
      if (!go) return;
      e.preventDefault();
      go();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  });

  const mine = p.comments.filter((c) => c.fileKey === (file?.key ?? '') || !c.fileKey);
  const roots = mine.filter((c) => !c.parentId).sort((a, b) => (a.frame ?? -1) - (b.frame ?? -1) || a.createdAt.localeCompare(b.createdAt));
  const doneCount = roots.filter((c) => c.done).length;
  const shown = roots.filter((c) => showDone || !c.done || c.id === active);
  const replies = (id: string) => mine.filter((c) => c.parentId === id);
  const activeC = roots.find((x) => x.id === active);
  const near = (c: CommentView | undefined) => Boolean(c && (c.frame === null || (frame >= c.frame - 1 && frame <= (c.endFrame ?? c.frame) + 1)));
  const overlay = draft ? { pin: draft.pin, drawing: draft.drawing } : activeC && near(activeC) ? { pin: activeC.pin, drawing: activeC.drawing ?? [] } : null;

  if (!file) return <Callout tone="warn" title={t.noPreview} />;
  const cantPlay = broken === file.key;
  // the people on the timeline (owner 2026-11-03, design B): one ball per comment, by its author
  const marks = roots.filter((c) => c.frame !== null && total > 0);
  return (
    <div class="rv">
      <div class="rv-stage" data-tour="player">
        <div class="rv-card rv-player">
          <div class={twin ? 'rv-pair' : undefined}>
            {twin ? (
              <figure class="rv-twin">
                <figcaption class="rv-meta">{p.compare!.label}</figcaption>
                {twin.kind === 'video' ? <video ref={other} key={twin.url} src={`${twin.url}#t=0.001`} preload="metadata" playsInline muted class="rv-media" /> : <img src={twin.url} alt={twin.name} class="rv-media" />}
              </figure>
            ) : null}
            <figure class="rv-twin">
              {twin ? <figcaption class="rv-meta">{p.thisLabel ?? t.thisOne}</figcaption> : null}
              <div class={`rv-screen${drawMode ? ' is-drawing' : ''}`} ref={screen}>
                {file.kind === 'video' ? (
                  <video ref={video} key={file.url} src={`${file.url}#t=0.001`} preload="metadata" playsInline class="rv-media" />
                ) : file.kind === 'image' ? (
                  <img src={file.url} alt={file.name} class="rv-media" />
                ) : (
                  <p class="rv-nopreview">{t.noPreview}</p>
                )}
                {file.kind !== 'other' ? (
                  <button
                    type="button"
                    class="rv-hit"
                    aria-label={drawMode ? t.drawing : p.canComment && (p.staff || started || file.kind !== 'video') ? t.clickHint : playing ? t.pause : t.play}
                    onClick={pick}
                    onPointerDown={down}
                    onPointerMove={move}
                    onPointerUp={up}
                    onPointerCancel={up}
                  />
                ) : null}
                {overlay ? (
                  <svg class="rv-ink" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                    {[...overlay.drawing.map((s) => s.points), ...(stroke ? [stroke] : [])].map((pts, i) => (
                      <path key={i} d={strokePath(pts)} />
                    ))}
                  </svg>
                ) : null}
                {file.kind === 'video' && !p.staff && !started && !loading && !playing && !draft ? (
                  <button type="button" class="rv-bigplay" onClick={toggle} aria-label={t.start}>
                    <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
                    </svg>
                  </button>
                ) : null}
                {file.kind === 'video' && loading && !cantPlay ? (
                  <span class="rv-loading" role="status">
                    <span class="rv-spinner" aria-hidden="true" />
                    <span class="sr-only">{t.loading}</span>
                  </span>
                ) : null}
                {overlay?.pin ? <span class="rv-pin" style={{ left: `${overlay.pin.x * 100}%`, top: `${overlay.pin.y * 100}%` }} aria-hidden="true" /> : null}
                {/* the controls sit in the picture (design B), so they follow it into full screen */}
                <div class="rv-ov">
                  {file.kind === 'video' ? (
                    <>
                      <button type="button" class="rv-ov-play" onClick={toggle} aria-label={playing ? t.pause : t.play}>
                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                          {playing ? <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor" /> : <path d="M8 5v14l11-7z" fill="currentColor" />}
                        </svg>
                      </button>
                      {p.staff ? (
                        <>
                          <button type="button" class="rv-ov-btn" onClick={() => seek(Math.max(0, frame - 1))} aria-label={t.back}>
                            ‹
                          </button>
                          <button type="button" class="rv-ov-btn" onClick={() => seek(frame + 1)} aria-label={t.fwd}>
                            ›
                          </button>
                        </>
                      ) : null}
                      <span class="rv-tc" aria-live="off">
                        {tc(frame)} <span class="rv-tc-total">/ {tc(total)}</span>
                      </span>
                    </>
                  ) : null}
                  <span class="rv-ov-gap" />
                  {p.files.length > 1 ? (
                    <span class="rv-files" role="tablist">
                      {p.files.map((f) => (
                        <button key={f.key} type="button" role="tab" aria-selected={f.key === file.key} class={f.key === file.key ? 'rv-file is-on' : 'rv-file'} onClick={() => (setFileKey(f.key), setDraft(null))}>
                          {f.format ?? f.name}
                        </button>
                      ))}
                    </span>
                  ) : null}
                  {file.downloadUrl ? (
                    <a class="rv-ov-btn rv-ov-dl" href={file.downloadUrl} download={file.name} title={t.download}>
                      {t.download}
                    </a>
                  ) : null}
                  {file.kind !== 'other' ? (
                    <button type="button" class="rv-ov-btn" onClick={fullscreen} aria-label={full ? t.exitFull : t.full} aria-pressed={full} title={full ? t.exitFull : t.full}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        {full ? <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" /> : <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />}
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>
            </figure>
          </div>
          {cantPlay ? <Callout tone="warn" title={p.staff ? t.cannotPlay : t.askKff} /> : null}
          {file.kind === 'other' && file.downloadUrl ? (
            <a class="btn btn-sm btn-ghost rv-dl" href={file.downloadUrl} download={file.name}>
              {t.download}
            </a>
          ) : null}
          {file.kind === 'video' ? (
            <div class="rv-bar">
              <input type="range" min={0} max={Math.max(total, 1)} value={frame} aria-label="Position" style={{ '--at': `${total ? (frame / total) * 100 : 0}%` }} onInput={(e) => seek(Number((e.currentTarget as HTMLInputElement).value))} />
              {marks
                .filter((c) => c.endFrame !== null)
                .map((c) => (
                  <span key={`r${c.id}`} class="rv-span" aria-hidden="true" style={{ left: `${((c.frame ?? 0) / total) * 100}%`, width: `${Math.max(0.6, (((c.endFrame ?? 0) - (c.frame ?? 0)) / total) * 100)}%` }} />
                ))}
              {marks.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  class={`rv-mark${c.done ? ' is-done' : ''}${c.internal ? ' is-internal' : ''}${c.endFrame !== null ? ' is-range' : ''}${c.id === active ? ' is-on' : ''}`}
                  style={{ left: `${((c.frame ?? 0) / total) * 100}%` }}
                  aria-label={`${tc(c.frame ?? 0)} ${c.author}`}
                  title={`${tc(c.frame ?? 0)} · ${c.author}`}
                  onClick={() => (setActive(c.id), seek(c.frame ?? 0))}
                >
                  <Avatar name={c.author} size="sm" client={!c.staff} decorative />
                </button>
              ))}
            </div>
          ) : null}
          {p.canComment ? (
            draft ? (
              <form class="rv-compose" onSubmit={submit}>
                <div class="rv-compose-head">
                  <strong>{draft.parentId ? t.reply : draft.frame !== null ? (draft.endFrame !== null ? t.range(tc(draft.frame), tc(draft.endFrame)) : t.at(tc(draft.frame))) : t.general}</strong>
                  {!draft.parentId && file.kind !== 'other' ? (
                    <span class="rv-compose-tools">
                      {draft.frame !== null && file.kind === 'video' ? (
                        draft.endFrame === null ? (
                          <button type="button" class="btn btn-sm btn-ghost" disabled={frame <= draft.frame} onClick={() => setDraft({ ...draft, endFrame: frame })}>
                            {t.endHere(tc(Math.max(frame, draft.frame)))}
                          </button>
                        ) : (
                          <button type="button" class="btn btn-sm btn-ghost" onClick={() => setDraft({ ...draft, endFrame: null })}>
                            {t.noEnd}
                          </button>
                        )
                      ) : null}
                      <button type="button" class={`btn btn-sm${drawMode ? ' btn-primary' : ' btn-ghost'}`} aria-pressed={drawMode} onClick={() => setDrawMode(!drawMode)}>
                        {t.draw}
                      </button>
                      {draft.drawing.length ? (
                        <button type="button" class="btn btn-sm btn-ghost" onClick={() => setDraft({ ...draft, drawing: [] })}>
                          {t.clearDrawing}
                        </button>
                      ) : null}
                    </span>
                  ) : null}
                </div>
                {drawMode ? <p class="rv-meta">{t.drawing}</p> : null}
                <textarea ref={box} class="input input--wide rv-text" rows={3} maxLength={4000} placeholder={t.write} value={text} onInput={(e) => setText((e.currentTarget as HTMLTextAreaElement).value)} />
                <div class="rv-compose-foot">
                  {p.staff && !draft.parentId ? (
                    <label class="rv-check">
                      <input type="checkbox" checked={internal} onChange={(e) => setInternal((e.currentTarget as HTMLInputElement).checked)} /> {t.internal}
                    </label>
                  ) : (
                    <span />
                  )}
                  <span class="rv-compose-btns">
                    <button type="button" class="btn btn-sm btn-ghost" onClick={() => (setDraft(null), setText(''), setDrawMode(false))}>
                      {t.cancel}
                    </button>
                    <button type="submit" class="btn btn-sm btn-primary" disabled={busy || !text.trim()}>
                      {t.send}
                    </button>
                  </span>
                </div>
              </form>
            ) : (
              // one calm row under the film (design B): the time it lands on, then the whole film as a link
              <div class="rv-say">
                {file.kind === 'video' ? <span class="rv-say-tc">{tc(frame)}</span> : null}
                <button type="button" class="rv-say-btn" onClick={() => start({ frame: file.kind === 'video' ? frame : null, pin: null, parentId: null })}>
                  {file.kind === 'video' ? t.sayAt : t.write}
                </button>
                <button type="button" class="linkish rv-say-all" onClick={() => start({ frame: null, pin: null, parentId: null })}>
                  {t.general}
                </button>
              </div>
            )
          ) : null}
        </div>
      </div>
      <div class="rv-col">
        {p.next}
        <section class="rv-side rv-card" aria-label={t.comments} data-tour="comments">
          <div class="rv-side-head">
            <h2 class="rv-h2">
              {t.comments} <span class="rv-count">{roots.length}</span>
            </h2>
            {doneCount ? (
              <span class="rv-seg" role="group" aria-label={t.comments}>
                <button type="button" aria-pressed={!showDone} onClick={() => setShowDone(false)}>
                  {t.open(roots.length - doneCount)}
                </button>
                <button type="button" aria-pressed={showDone} onClick={() => setShowDone(true)}>
                  {t.all(roots.length)}
                </button>
              </span>
            ) : null}
          </div>
          {!shown.length ? <p class="rv-empty">{roots.length ? t.allDone : t.noComments}</p> : null}
          <ol class="rv-list">
            {shown.map((c) => (
              <li key={c.id} class={`rv-c${c.id === active ? ' is-active' : ''}${c.done ? ' is-done' : ''}${c.internal && p.staff ? ' is-internal' : ''}`}>
                <Thread c={c} replies={replies(c.id)} lang={p.lang} staff={p.staff} canComment={p.canComment} onOpen={() => (setActive(c.id), c.frame !== null && seek(c.frame))} onReply={() => (setActive(c.id), start({ frame: null, pin: null, parentId: c.id }))} done={p.done} remove={p.remove} />
              </li>
            ))}
          </ol>
          {p.previous && p.previous.comments.length ? (
            <details class="rv-prev">
              <summary>
                {t.previous(p.previous.number)} · {t.fixed(p.previous.comments.filter((c) => c.done).length, p.previous.comments.length)}
              </summary>
              <ul class="rv-prev-list">
                {p.previous.comments.map((c) => (
                  <li key={c.id} class={c.done ? 'is-done' : undefined}>
                    <span aria-hidden="true">{c.done ? '✓' : '○'}</span> {c.frame !== null ? <span class="rv-c-tc">{tc(c.frame, c.fps ?? FPS)}</span> : null} <strong>{c.author}</strong>: {c.body}
                    {c.done ? <span class="sr-only"> ({t.done})</span> : null}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function Thread(p: { c: CommentView; replies: CommentView[]; lang: Lang; staff: boolean; canComment: boolean; onOpen: () => void; onReply: () => void; done?: PlayerProps['done']; remove?: PlayerProps['remove'] }) {
  const t = T[p.lang];
  const tc = (f: number, fps = FPS) => (p.staff ? timecode(f, fps) : clock(f, fps));
  const run = (f: () => Promise<void>) => () => void f().catch((e) => toast({ text: errText(e) }));
  const one = (c: CommentView, root: boolean) => (
    <div class="rv-c-body">
      <div class="rv-c-head">
        <Avatar name={c.author} size="sm" client={!c.staff} decorative />
        <span class="rv-c-who">{c.author}</span>
        {root && c.frame !== null ? (
          <button type="button" class="rv-c-tc" onClick={p.onOpen}>
            {tc(c.frame, c.fps ?? FPS)}
            {c.endFrame !== null ? `–${tc(c.endFrame, c.fps ?? FPS)}` : ''}
          </button>
        ) : null}
        {root && c.drawing?.length ? <span class="rv-c-ink" aria-label={t.draw} title={t.draw}>✎</span> : null}
        {c.internal && p.staff ? <Pill tone="warn">{t.internalTag}</Pill> : null}
        {root && p.staff && p.done ? (
          <button type="button" class="rv-tick" aria-pressed={c.done} aria-label={t.done} title={c.done ? t.undone : t.done} onClick={run(() => p.done!(c.id, !c.done))}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
          </button>
        ) : null}
      </div>
      <p class="rv-c-text" onClick={root ? p.onOpen : undefined}>
        {c.body}
        {c.edited ? <span class="rv-c-edited"> ({t.edited})</span> : null}
      </p>
      <div class="rv-c-acts">
        {root && p.canComment ? (
          <button type="button" class="linkish" onClick={p.onReply}>
            {t.reply}
          </button>
        ) : null}
        {c.mine && p.remove ? (
          <button type="button" class="linkish" onClick={run(() => p.remove!(c.id))}>
            {t.remove}
          </button>
        ) : null}
      </div>
    </div>
  );
  return (
    <>
      {one(p.c, true)}
      {p.replies.length ? (
        <ol class="rv-replies">
          {p.replies.map((r) => (
            <li key={r.id}>{one(r, false)}</li>
          ))}
        </ol>
      ) : null}
    </>
  );
}

/* ---------------- the staff page ---------------- */

type Stage = 'internal' | 'producer' | 'client' | 'closed';
interface StaffData {
  project: { id: string; name: string };
  video: { id: string; name: string; fromMonday: boolean };
  versions: { id: string; number: number; phase: 'draft' | 'final'; stage: Stage; outcome: string | null; clientRound: number | null; createdAt: string }[];
  version: null | {
    id: string;
    number: number;
    phase: 'draft' | 'final';
    stage: Stage;
    outcome: 'approved' | 'approved_minor' | 'changes' | 'superseded' | 'withdrawn' | null;
    clientRound: number | null;
    sender: string | null;
    files: FileView[];
    comments: CommentView[];
    decisions: { stage: Stage; by: string; decision: string; forName: string | null; reason: string | null; at: string }[];
    can: { internal: boolean; forCd: boolean; producer: boolean; deliver?: boolean; withdraw?: boolean; remove?: boolean };
    deliveredAt?: string | null;
    deliverTo?: string[];
    /** the project's other approved films that can go in the same delivery mail */
    deliverMore?: string[];
    /** how much the Hub emails the client here (owner 2026-10-24), and who turned it down */
    clientMail?: { level: MailLevel; by: MutedBy | null };
    reminders?: { sent: number; max: number; paused: boolean; next: string | null; canPause: boolean } | null;
    waitingOn?: string[];
    contacts: { id: string; name: string; email: string; decider: boolean; byEmail?: boolean }[];
    previous?: Previous | null;
    compare?: { id: string; number: number; files: FileView[] } | null;
  };
  /** the video's chains copied from Ziflow before it ended (read only) */
  ziflow?: ZfProof[];
  /** on the crew's link: the viewer edits this video and may send a new version */
  canSend?: boolean;
}
interface ZfComment {
  id: string;
  author: string;
  internal: boolean;
  startMs: number | null;
  endMs: number | null;
  text: string;
  at: string | null;
  replies: ZfComment[];
}
interface ZfProof {
  id: string;
  name: string;
  createdAt: string | null;
  versions: { version: string; versionId: string; files: string[]; comments: ZfComment[] }[];
  decisions: { stage: string; name: string; decision: string; at: string | null }[];
}
type MailLevel = 'on' | 'no_reminders' | 'off';
type MutedBy = 'company' | 'client' | 'project';
interface Mail {
  to: string;
  name: string | null;
  subject: string;
  body: string;
  url: string;
  emailed?: boolean;
  error?: string | null;
  /** not emailed on purpose: this send only, or the project's, client's or company's choice */
  muted?: MutedBy | 'send' | null;
}

const S = texts(
  {
    review: 'Review',
    version: (n: number) => `Version ${n}`,
    final: 'Final formats',
    draft: 'Draft',
    stage: { internal: 'With the CD', producer: 'With the producer', client: 'With the client', closed: 'Closed' } as Record<Stage, string>,
    outcome: { approved: 'Approved', approved_minor: 'Approved with small adjustments', changes: 'Changes requested', superseded: 'Replaced by a newer version', withdrawn: 'Withdrawn' } as Record<string, string>,
    none: 'Nothing has been sent for review in the Hub yet.',
    toFiles: 'Go to Files',
    sendFile: 'Send a file for review',
    approve: 'Approve',
    changes: 'Request changes',
    sendClient: 'Approve and send to client',
    reason: 'Not everyone has approved yet – why decide now?',
    to: 'Who approves at the client',
    toInfo: 'Everyone you tick has to approve. The Hub emails each of them their own link.',
    noEmail: 'no email from the Hub here – copy the text',
    sentBy: (n: string) => `Sent by ${n}`,
    waitingOn: (n: string) => `Waiting for ${n}`,
    decisions: 'Decisions',
    forName: (n: string) => `for ${n}`,
    copyLink: 'Copy link',
    copyMail: 'Copy email',
    copied: 'Copied',
    mailTitle: 'Send this to the client',
    mailInfo: 'The Hub could not email this person: copy the text into your own email.',
    emailed: (n: string) => `Emailed to ${n}.`,
    waitClient: 'Waiting for the client’s decision.',
    monday: 'The status in Monday is set by the Hub.',
    back: 'Back to the project',
    round: (n: number) => `round ${n}`,
    deliver: 'Deliver to the client',
    deliverInfo: (to: string) => (to ? `The Hub emails ${to} a link to download the files and marks the video Finished.` : 'Mark a contact on the client card to receive deliveries first.'),
    delivered: 'Delivered to the client.',
    crewNone: 'Nothing has been sent for review in the Hub yet.',
    noEmailSend: 'Don’t email, I’ll send it myself',
    noEmailHint: 'You get the text and the link to copy into your own email.',
    mutedInfo: (by: MutedBy | 'send') => (by === 'send' ? 'Not emailed, as you chose: copy the text into your own email.' : `Emails to the client are off for ${MUTED_EN[by]}: copy the text into your own email.`),
    mutedNote: (by: MutedBy) => `Emails to the client are off for ${MUTED_EN[by]}. You get the text to copy instead.`,
    remindNext: (n: number, max: number, at: string) => `Reminder ${n} of ${max} goes ${at}.`,
    remindPaused: 'Reminders to the client are paused.',
    remindDone: (max: number) => `All ${max} reminders have gone out.`,
    remindMuted: (by: MutedBy) => `No reminders: turned off for ${MUTED_EN[by]}.`,
    remindNoSender: 'No reminders: the Hub cannot email from here.',
    pause: 'Pause reminders',
    resume: 'Resume reminders',
    paused: 'Reminders paused',
    resumed: 'Reminders resumed',
    deliverAll: (n: number) => `Deliver all ${n} approved`,
    deliverOne: 'Deliver only this one',
    deliverAllInfo: (names: string) => `${names} ${names.includes(',') ? 'are' : 'is'} approved too and can go in the same email.`,
    deliveredAll: 'Every film in the project is delivered.',
    steps: ['Editing', 'CD', 'Producer', 'Client', 'Delivery'],
    stepsLabel: 'Where the version is',
    next: 'Your next step',
    history: 'Decisions and history'
  },
  {
    review: 'Granskning',
    version: (n: number) => `Version ${n}`,
    final: 'Slutformat',
    draft: 'Utkast',
    stage: { internal: 'Hos CD', producer: 'Hos producenten', client: 'Hos kunden', closed: 'Stängd' } as Record<Stage, string>,
    outcome: { approved: 'Godkänd', approved_minor: 'Godkänd med små justeringar', changes: 'Ändringar begärda', superseded: 'Ersatt av en nyare version', withdrawn: 'Tillbakadragen' } as Record<string, string>,
    none: 'Inget har skickats för granskning i Hubben ännu.',
    toFiles: 'Gå till Filer',
    sendFile: 'Skicka en fil för granskning',
    approve: 'Godkänn',
    changes: 'Begär ändringar',
    sendClient: 'Godkänn och skicka till kund',
    reason: 'Alla har inte godkänt ännu – varför besluta nu?',
    to: 'Vem godkänner hos kunden',
    toInfo: 'Alla du bockar i måste godkänna. Hubben mejlar var och en sin egen länk.',
    noEmail: 'Hubben mejlar inte härifrån – kopiera texten',
    sentBy: (n: string) => `Skickad av ${n}`,
    waitingOn: (n: string) => `Väntar på ${n}`,
    decisions: 'Beslut',
    forName: (n: string) => `för ${n}`,
    copyLink: 'Kopiera länk',
    copyMail: 'Kopiera mejl',
    copied: 'Kopierat',
    mailTitle: 'Skicka det här till kunden',
    mailInfo: 'Hubben kunde inte mejla den här personen: kopiera texten till ditt eget mejl.',
    emailed: (n: string) => `Mejlat till ${n}.`,
    waitClient: 'Väntar på kundens beslut.',
    monday: 'Statusen i Monday sätts av Hubben.',
    back: 'Tillbaka till projektet',
    round: (n: number) => `runda ${n}`,
    deliver: 'Leverera till kunden',
    deliverInfo: (to: string) => (to ? `Hubben mejlar ${to} en länk för att ladda ner filerna och markerar videon som levererad.` : 'Markera först en kontakt på kundkortet som tar emot leveranser.'),
    delivered: 'Levererad till kunden.',
    crewNone: 'Inget har skickats för granskning i Hubben ännu.',
    noEmailSend: 'Mejla inte, jag skickar själv',
    noEmailHint: 'Du får texten och länken att kopiera till ditt eget mejl.',
    mutedInfo: (by: MutedBy | 'send') => (by === 'send' ? 'Inte mejlat, som du valde: kopiera texten till ditt eget mejl.' : `Utskick till kunden är avstängda för ${MUTED_SV[by]}: kopiera texten till ditt eget mejl.`),
    mutedNote: (by: MutedBy) => `Utskick till kunden är avstängda för ${MUTED_SV[by]}. Du får texten att kopiera i stället.`,
    remindNext: (n: number, max: number, at: string) => `Påminnelse ${n} av ${max} skickas ${at}.`,
    remindPaused: 'Påminnelserna till kunden är pausade.',
    remindDone: (max: number) => `Alla ${max} påminnelser har skickats.`,
    remindMuted: (by: MutedBy) => `Inga påminnelser: avstängda för ${MUTED_SV[by]}.`,
    remindNoSender: 'Inga påminnelser: Hubben kan inte mejla härifrån.',
    pause: 'Pausa påminnelser',
    resume: 'Återuppta påminnelser',
    paused: 'Påminnelser pausade',
    resumed: 'Påminnelser återupptagna',
    deliverAll: (n: number) => `Leverera alla ${n} godkända`,
    deliverOne: 'Leverera bara den här',
    deliverAllInfo: (names: string) => `${names} är också ${names.includes(',') ? 'godkända' : 'godkänd'} och kan följa med i samma mejl.`,
    deliveredAll: 'Alla filmer i projektet är levererade.',
    steps: ['Redigering', 'CD', 'Producent', 'Kund', 'Leverans'],
    stepsLabel: 'Var versionen är',
    next: 'Ditt nästa steg',
    history: 'Beslut och historik'
  }
);

const MUTED_EN: Record<MutedBy, string> = { project: 'this project', client: 'this client', company: 'the whole company' };
const MUTED_SV: Record<MutedBy, string> = { project: 'det här projektet', client: 'den här kunden', company: 'hela bolaget' };

/** "Thursday 22 Oct at 08:00": when the next reminder goes. */
const whenText = (iso: string, lang: Lang) => {
  const d = new Date(iso);
  const day = d.toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'Europe/Stockholm' });
  const time = d.toLocaleTimeString(lang === 'sv' ? 'sv-SE' : 'en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Stockholm' });
  return lang === 'sv' ? `${day} kl. ${time}` : `${day} at ${time}`;
};

const copy = async (s: string, ok: string) => {
  try {
    await navigator.clipboard.writeText(s);
    toast({ text: ok });
  } catch {
    // no clipboard (insecure context): the text is on screen to copy by hand
  }
};

/**
 * Light or dark review (owner 2026-11-03): a switch on the review page, for staff and client alike, remembered in this
 * browser. It borrows the Hub's own dark theme while the page is open and puts the person's choice back on the way out.
 */
const THEME_KEY = 'kff.reviewTheme';
type ReviewTheme = 'light' | 'dark';
const systemDark = () => {
  const root = document.documentElement.getAttribute('data-theme');
  if (root === 'dark' || root === 'light') return root === 'dark';
  return Boolean(window.matchMedia?.('(prefers-color-scheme: dark)')?.matches);
};
export function useReviewTheme() {
  const [mode, setMode] = useState<ReviewTheme | null>(() => {
    try {
      const v = localStorage.getItem(THEME_KEY);
      return v === 'light' || v === 'dark' ? v : null;
    } catch {
      return null;
    }
  });
  const [base] = useState(systemDark);
  useEffect(() => {
    if (!mode) return;
    const root = document.documentElement;
    const before = root.getAttribute('data-theme');
    root.setAttribute('data-theme', mode);
    return () => (before === null ? root.removeAttribute('data-theme') : root.setAttribute('data-theme', before));
  }, [mode]);
  const dark = mode ? mode === 'dark' : base;
  const toggle = () => {
    const next: ReviewTheme = dark ? 'light' : 'dark';
    setMode(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // private window: the switch still works for this visit
    }
  };
  return { dark, toggle };
}

/** The light/dark switch in the review's title row. */
function ThemeSwitch(p: { lang: Lang; theme: ReturnType<typeof useReviewTheme> }) {
  const t = T[p.lang];
  return (
    <span class="rv-seg rv-theme" role="group" aria-label={t.themeLabel}>
      <button type="button" aria-pressed={!p.theme.dark} onClick={() => p.theme.dark && p.theme.toggle()}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
        {t.light}
      </button>
      <button type="button" aria-pressed={p.theme.dark} onClick={() => !p.theme.dark && p.theme.toggle()}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
        {t.dark}
      </button>
    </span>
  );
}

/** Where the version is in the chain (design A's step row): editing › CD › producer › client › delivery. */
const STEP_OF = (v: { stage: Stage; outcome: string | null; deliveredAt?: string | null }): number | null =>
  v.stage === 'internal' ? 1 : v.stage === 'producer' ? 2 : v.stage === 'client' ? 3 : v.outcome === 'changes' ? 0 : v.outcome === 'approved' || v.outcome === 'approved_minor' ? (v.deliveredAt ? 5 : 4) : null;
function Steps(p: { lang: Lang; at: number | null }) {
  const s = S[p.lang];
  if (p.at === null) return null;
  return (
    <ol class="rv-steps" aria-label={s.stepsLabel}>
      {s.steps.map((name, i) => (
        <li key={name} class={i < p.at! ? 'is-done' : i === p.at ? 'is-now' : undefined} aria-current={i === p.at ? 'step' : undefined}>
          <span class="rv-step-dot" aria-hidden="true">
            {i < p.at! ? '✓' : i + 1}
          </span>
          {name}
        </li>
      ))}
    </ol>
  );
}

/**
 * `crew`: the same page on the crew's own link (services/crewReview.ts): every comment, ticks and answers, a new version
 * from the video's editor, and no decisions. The page switches versions in place, since the link has no routes.
 */
export function StaffReview(p: { api: Api; projectId: string; videoId: string; version: string | null; crew?: { back: () => void } }) {
  const lang = useLang();
  const s = S[lang];
  const t = T[lang];
  const theme = useReviewTheme();
  const [data, setData] = useState<StaffData | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [mails, setMails] = useState<Mail[]>([]);
  const [reason, setReason] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [compare, setCompare] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(p.version);
  const [panelKey, setPanelKey] = useState(false);
  const [noEmail, setNoEmail] = useState(false);
  const [sendFile, setSendFile] = useState(false);
  useEffect(() => setVer(p.version), [p.version]);
  const base = p.crew ? `/api/crew/projects/${p.projectId}` : `/api/projects/${p.projectId}`;
  const q = new URLSearchParams({ ...(ver ? { version: ver } : {}), ...(compare ? { compare } : {}) }).toString();
  const load = () =>
    p.api.get<StaffData>(`${base}/videos/${p.videoId}/review${q ? `?${q}` : ''}`).then(
      (d) => (setData(d), setErr(null)),
      (e) => setErr(e)
    );
  useEffect(() => {
    void load();
  }, [p.projectId, p.videoId, ver, compare]);
  useEffect(() => {
    if (data) document.title = `${data.video.name} · ${s.review}`;
  }, [data?.video.name]);
  const v = data?.version ?? null;
  useEffect(() => {
    const ds = v?.contacts.filter((c) => c.decider) ?? [];
    setPicked((ds.length ? ds : v?.contacts.slice(0, 1) ?? []).map((c) => c.id));
  }, [v?.id]);
  if (err) return <ErrorBox error={err} onRetry={() => void load()} />;
  if (!data) return <Loading />;
  const decide = async (decision: 'approved' | 'changes', to?: 'client') => {
    if (!v) return;
    setBusy(true);
    try {
      const r = await p.api.post<{ stage: Stage; mail: Mail | null; mails?: Mail[] }>(`${base}/review/${v.id}/decide`, {
        decision,
        ...(to ? { to, contactIds: picked, ...(noEmail ? { noEmail: true } : {}) } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {})
      });
      setMails(r.mails ?? (r.mail ? [r.mail] : []));
      setReason('');
      setNoEmail(false);
      await load();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  const deliver = async (all = false) => {
    if (!v) return;
    setBusy(true);
    try {
      const r = await p.api.post<{ mails: Mail[]; allDone?: boolean }>(`${base}/review/${v.id}/deliver`, { ...(all ? { all: true } : {}), ...(noEmail ? { noEmail: true } : {}) });
      setMails(r.mails ?? []);
      setNoEmail(false);
      if (r.allDone) toast({ text: s.deliveredAll });
      await load();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  const pauseReminders = async (paused: boolean) => {
    if (!v) return;
    try {
      await p.api.post(`${base}/review/${v.id}/reminders`, { paused });
      toast({ text: paused ? s.paused : s.resumed });
      await load();
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  const mutedBy = v?.clientMail && v.clientMail.level === 'off' ? v.clientMail.by : null;
  const reminderText = (r: NonNullable<NonNullable<StaffData['version']>['reminders']>, mail: { level: MailLevel; by: MutedBy | null } | null) =>
    r.paused ? s.remindPaused : r.next ? s.remindNext(r.sent + 1, r.max, whenText(r.next, lang)) : r.sent >= r.max ? s.remindDone(r.max) : mail && mail.level !== 'on' && mail.by ? s.remindMuted(mail.by) : s.remindNoSender;
  const noEmailBox = (id: string) =>
    mutedBy ? (
      <p class="rv-meta">{s.mutedNote(mutedBy)}</p>
    ) : (
      <MoreOptions id={id} filled={noEmail ? 1 : 0}>
        <label class="rv-check">
          <input type="checkbox" checked={noEmail} onChange={(e) => setNoEmail((e.currentTarget as HTMLInputElement).checked)} aria-describedby={`${id}-hint`} /> {s.noEmailSend}
        </label>
        <p class="rv-meta" id={`${id}-hint`}>
          {s.noEmailHint}
        </p>
      </MoreOptions>
    );
  const open = (id: string) => (p.crew ? setVer(id) : navigate(`/projects/${p.projectId}/review/${p.videoId}?version=${id}`));
  const others = data.versions.filter((x) => x.id !== v?.id);
  const emailed = mails.filter((m) => m.emailed);
  const toCopy = mails.filter((m) => !m.emailed);
  const stageTone = (x: { stage: Stage; outcome: string | null }) => (x.stage === 'closed' ? (x.outcome === 'changes' ? 'warn' : x.outcome === 'superseded' || x.outcome === 'withdrawn' ? 'neutral' : 'ok') : 'info');
  const stageText = (x: { stage: Stage; outcome: string | null }) => (x.stage === 'closed' && x.outcome ? s.outcome[x.outcome] : s.stage[x.stage]);
  // the decision for the stage the version is in, as one card above the comments (design B)
  const next = !v ? null : v.can.internal || v.can.producer ? (
    <section class="rv-decide rv-card rv-next is-yours" data-tour="decide" aria-label={s.decisions}>
      <p class="rv-kicker">{s.next}</p>
      {v.can.forCd ? (
        <label class="field">
          <span>{s.reason}</span>
          <input class="input input--wide" maxLength={500} value={reason} onInput={(e) => setReason((e.currentTarget as HTMLInputElement).value)} />
        </label>
      ) : null}
      {v.can.producer && v.contacts.length ? (
        <fieldset class="rv-who">
          <legend>{s.to}</legend>
          <p class="rv-meta">{s.toInfo}</p>
          {v.contacts.map((c) => (
            <label key={c.id} class="rv-check">
              <input type="checkbox" checked={picked.includes(c.id)} onChange={(e) => setPicked((e.currentTarget as HTMLInputElement).checked ? [...picked, c.id] : picked.filter((x) => x !== c.id))} /> {c.name}
              {c.name !== c.email ? <span class="rv-meta"> {c.email}</span> : null}
              {c.byEmail === false ? <span class="rv-meta"> · {s.noEmail}</span> : null}
            </label>
          ))}
        </fieldset>
      ) : null}
      <div class="rv-row">
        {v.can.producer ? (
          <button type="button" class="btn btn-primary" disabled={busy || (v.contacts.length > 0 && !picked.length) || (v.can.forCd && !reason.trim())} onClick={() => void decide('approved', 'client')}>
            {s.sendClient}
          </button>
        ) : null}
        {v.stage === 'internal' && v.can.internal && !v.can.forCd ? (
          <button type="button" class={v.can.producer ? 'btn' : 'btn btn-primary'} disabled={busy} onClick={() => void decide('approved')}>
            {s.approve}
          </button>
        ) : null}
        <button type="button" class="btn" disabled={busy || (v.can.forCd && !reason.trim())} onClick={() => void decide('changes')}>
          {s.changes}
        </button>
      </div>
      {v.can.producer && v.contacts.length ? noEmailBox('rv-send') : null}
    </section>
  ) : v.stage === 'client' ? (
    <section class="rv-decide rv-card rv-next" data-tour="decide" aria-label={s.waitClient}>
      <p class="rv-kicker">{s.stage.client}</p>
      <p class="rv-next-h">{s.waitClient}</p>
      {v.waitingOn?.length ? <p class="rv-meta">{s.waitingOn(v.waitingOn.join(', '))}</p> : null}
      {v.reminders ? <p class="rv-meta rv-remind">{reminderText(v.reminders, v.clientMail ?? null)}</p> : null}
      {v.reminders?.canPause && (v.reminders.paused || v.reminders.next) ? (
        <MoreOptions id="rv-remind">
          <div class="rv-row">
            <button type="button" class="btn btn-sm" onClick={() => void pauseReminders(!v.reminders!.paused)}>
              {v.reminders.paused ? s.resume : s.pause}
            </button>
          </div>
        </MoreOptions>
      ) : null}
    </section>
  ) : v.can.deliver ? (
    <section class="rv-decide rv-card rv-next is-yours" data-tour="decide" aria-label={s.deliver}>
      <p class="rv-kicker">{s.next}</p>
      <p class="rv-meta">{s.deliverInfo((v.deliverTo ?? []).join(', '))}</p>
      {v.deliverMore?.length ? <p class="rv-meta">{s.deliverAllInfo(v.deliverMore.join(', '))}</p> : null}
      <div class="rv-row">
        {v.deliverMore?.length ? (
          <>
            <button type="button" class="btn btn-primary" disabled={busy || !v.deliverTo?.length} onClick={() => void deliver(true)}>
              {s.deliverAll(v.deliverMore.length + 1)}
            </button>
            <button type="button" class="btn" disabled={busy || !v.deliverTo?.length} onClick={() => void deliver()}>
              {s.deliverOne}
            </button>
          </>
        ) : (
          <button type="button" class="btn btn-primary" disabled={busy || !v.deliverTo?.length} onClick={() => void deliver()}>
            {s.deliver}
          </button>
        )}
      </div>
      {v.deliverTo?.length ? noEmailBox('rv-deliver') : null}
    </section>
  ) : v.deliveredAt ? (
    <section class="rv-card rv-next">
      <p class="rv-next-h">{s.delivered}</p>
    </section>
  ) : null;
  return (
    <div class="rv-page">
      <nav class="rv-crumbs">
        <a href={p.crew ? '#' : `/projects/${p.projectId}`} onClick={(e) => (e.preventDefault(), p.crew ? p.crew.back() : navigate(`/projects/${p.projectId}`))}>
          {data.project.name}
        </a>
        <span aria-hidden="true"> › </span>
        <span>{data.video.name}</span>
      </nav>
      <header class="rv-head">
        <div class="rv-title">
          <h1 class="rv-h1">
            <span class="sr-only">{s.review}: </span>
            {data.video.name}
          </h1>
          {v ? <Pill tone={stageTone(v)}>{stageText(v)}</Pill> : null}
        </div>
        <span class="rv-row rv-tools" data-tour="versions">
          {data.versions.length > 1 && data.versions.length <= 5 ? (
            <span class="rv-seg rv-vtabs" role="group" aria-label={s.review}>
              {data.versions.map((x) => (
                <button key={x.id} type="button" aria-pressed={x.id === v?.id} title={`${x.phase === 'final' ? s.final : s.draft} · ${stageText(x)}`} onClick={() => x.id !== v?.id && open(x.id)}>
                  {s.version(x.number)}
                </button>
              ))}
            </span>
          ) : data.versions.length > 1 ? (
            <select class="input rv-versions" aria-label={s.review} value={v?.id ?? ''} onChange={(e) => open((e.currentTarget as HTMLSelectElement).value)}>
              {data.versions.map((x) => (
                <option key={x.id} value={x.id}>
                  {s.version(x.number)} · {x.phase === 'final' ? s.final : s.draft} · {stageText(x)}
                </option>
              ))}
            </select>
          ) : null}
          {others.length && v ? (
            <select class="input rv-versions" aria-label={t.compare} value={compare} onChange={(e) => setCompare((e.currentTarget as HTMLSelectElement).value)}>
              <option value="">{t.noCompare}</option>
              {others.map((x) => (
                <option key={x.id} value={x.id}>
                  {t.compare} {s.version(x.number).toLowerCase()}
                </option>
              ))}
            </select>
          ) : null}
          <ThemeSwitch lang={lang} theme={theme} />
          {p.crew && data.canSend ? (
            <button type="button" class="btn btn-sm" data-tour="panel" onClick={() => setPanelKey(true)}>
              {PANEL_TEXT[lang].menu}
            </button>
          ) : null}
          {/* withdraw, or remove a withdrawn version for good (owner 2026-11-02); staff only, not the crew's page */}
          {v && !p.crew ? <VersionMenu api={p.api} base={base} version={v} onDone={(removed) => (removed && ver ? navigate(`/projects/${p.projectId}/review/${p.videoId}`) : void load())} /> : null}
        </span>
      </header>
      {v ? (
        <div class="rv-status">
          <Steps lang={lang} at={STEP_OF(v)} />
          <span class="rv-meta">
            {s.version(v.number)} · {v.phase === 'final' ? s.final : s.draft}
            {v.clientRound ? ` · ${s.round(v.clientRound)}` : ''}
            {v.sender ? ` · ${s.sentBy(v.sender)}` : ''}
            {v.waitingOn?.length && v.stage !== 'closed' ? ` · ${s.waitingOn(v.waitingOn.join(', '))}` : ''}
          </span>
        </div>
      ) : null}
      {sendFile ? <SendForReview api={p.api} projectId={p.projectId} videoId={p.videoId} videoName={data.video.name} fallback={async () => navigate(`/projects/${p.projectId}/files`)} onDone={async () => void (await load())} onClose={() => setSendFile(false)} /> : null}
      {panelKey ? <PanelKeyDialog api={p.api} path="/api/crew/panel-token" onClose={() => setPanelKey(false)} /> : null}
      {p.crew && data.canSend ? (
        <Tip id="review-panel" title={TIPS[lang].panelTitle}>
          {TIPS[lang].panel}
        </Tip>
      ) : null}
      {p.crew && data.canSend ? <CrewSend api={p.api} base={base} videoId={p.videoId} onSent={(id) => setVer(id)} /> : null}
      {!v ? (
        p.crew ? (
          <Callout tone="info" title={s.crewNone} />
        ) : (
          <Callout tone="info" title={s.none}>
            {/* pick the export (or upload it) right here (owner 2026-11-01); the Files tab stays a link */}
            <button type="button" class="btn btn-sm btn-primary" onClick={() => setSendFile(true)}>
              {s.sendFile}
            </button>{' '}
            <button type="button" class="btn btn-sm" onClick={() => navigate(`/projects/${p.projectId}/files`)}>
              {s.toFiles}
            </button>
          </Callout>
        )
      ) : (
        <>
          {emailed.length ? <Callout tone="ok" title={s.emailed(emailed.map((m) => m.name ?? m.to).join(', '))} /> : null}
          {toCopy.map((mail) => (
            <section key={mail.to} class="rv-mail" aria-label={s.mailTitle}>
              <h2 class="rv-h2">{s.mailTitle}</h2>
              <p class="rv-meta">{mail.muted ? s.mutedInfo(mail.muted) : s.mailInfo}</p>
              <p>
                <strong>{mail.name ?? mail.to}</strong> &lt;{mail.to}&gt; · {mail.subject}
              </p>
              <pre class="rv-mail-body">{mail.body}</pre>
              <div class="rv-row">
                <button type="button" class="btn btn-sm btn-primary" onClick={() => void copy(mail.body, s.copied)}>
                  {s.copyMail}
                </button>
                <button type="button" class="btn btn-sm" onClick={() => void copy(mail.url, s.copied)}>
                  {s.copyLink}
                </button>
              </div>
            </section>
          ))}
          <ReviewPlayer
            key={`${v.id}:${v.compare?.id ?? ''}`}
            lang={lang}
            files={v.files}
            comments={v.comments}
            staff
            canComment={v.stage !== 'closed'}
            previous={v.previous}
            compare={v.compare ? { label: t.other(v.compare.number), files: v.compare.files } : null}
            thisLabel={t.other(v.number)}
            next={next}
            add={async (c) => {
              await p.api.post(`${base}/review/${v.id}/comments`, c);
              await load();
            }}
            done={async (id, done) => {
              await p.api.put(`${base}/review/comments/${id}`, { done });
              await load();
            }}
            remove={async (id) => {
              await p.api.put(`${base}/review/comments/${id}`, { delete: true });
              await load();
            }}
          />
          {v.decisions.length || data.video.fromMonday ? (
            <details class="rv-history">
              <summary>{s.history}</summary>
              {v.decisions.length ? (
                <section class="rv-decisions">
                  <h2 class="rv-h2">{s.decisions}</h2>
                  <ul class="rv-dlist">
                    {v.decisions.map((d, i) => (
                      <li key={i}>
                        <strong>{d.by}</strong> {d.forName ? `(${s.forName(d.forName)}) ` : ''}· {s.outcome[d.decision] ?? d.decision} · {s.stage[d.stage]}
                        {d.reason ? <span class="rv-meta"> – {d.reason}</span> : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {data.video.fromMonday ? <p class="rv-meta">{s.monday}</p> : null}
            </details>
          ) : null}
        </>
      )}
      {data.ziflow?.length ? <ZiflowHistory proofs={data.ziflow} lang={lang} /> : null}
    </div>
  );
}

const Z = texts(
  {
    title: 'From Ziflow',
    info: 'Copied from Ziflow before it ended. Read only.',
    version: (v: string) => (v ? `Version ${v}` : 'Version'),
    comments: (n: number) => `${n} ${n === 1 ? 'comment' : 'comments'}`,
    internal: 'internal',
    general: 'General',
    decision: { approved: 'Approved', approved_with_changes: 'Approved with changes', changes_required: 'Changes required', not_approved: 'Not approved' } as Record<string, string>
  },
  {
    title: 'Från Ziflow',
    info: 'Kopierat från Ziflow innan det avslutades. Kan bara läsas.',
    version: (v: string) => (v ? `Version ${v}` : 'Version'),
    comments: (n: number) => `${n} ${n === 1 ? 'kommentar' : 'kommentarer'}`,
    internal: 'intern',
    general: 'Allmänt',
    decision: { approved: 'Godkänd', approved_with_changes: 'Godkänd med ändringar', changes_required: 'Ändringar krävs', not_approved: 'Inte godkänd' } as Record<string, string>
  }
);

/** Ziflow keeps time in milliseconds; shown as the Hub's timecode. */
const tcMs = (ms: number) => timecode(Math.round((ms * FPS) / 1000));

function ZfLine(p: { c: ZfComment; lang: Lang }) {
  const z = Z[p.lang];
  return (
    <>
      <span class="rv-c-tc">{p.c.startMs !== null ? `${tcMs(p.c.startMs)}${p.c.endMs !== null && p.c.endMs > p.c.startMs ? `–${tcMs(p.c.endMs)}` : ''}` : z.general}</span> <strong>{p.c.author}</strong>
      {p.c.internal ? <span class="rv-meta"> ({z.internal})</span> : null}: {p.c.text}
      {p.c.replies.length ? (
        <ul class="rv-prev-list">
          {p.c.replies.map((r) => (
            <li key={r.id}>
              <strong>{r.author}</strong>: {r.text}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function ZiflowHistory(p: { proofs: ZfProof[]; lang: Lang }) {
  const z = Z[p.lang];
  return (
    <section class="rv-zf" aria-label={z.title}>
      <h2 class="rv-h2">{z.title}</h2>
      <p class="rv-meta">{z.info}</p>
      {p.proofs.map((pr) => (
        <div key={pr.id} class="rv-zf-proof">
          <h3 class="rv-h3">{pr.name}</h3>
          {pr.decisions.length ? (
            <ul class="rv-dlist">
              {pr.decisions.map((d, i) => (
                <li key={i}>
                  <strong>{d.name}</strong> · {z.decision[d.decision] ?? d.decision}
                  {d.stage ? <span class="rv-meta"> · {d.stage}</span> : null}
                  {d.at ? <span class="rv-meta"> · {d.at.slice(0, 10)}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {pr.versions.map((v) => (
            <details key={v.versionId} class="rv-prev">
              <summary>
                {z.version(v.version)} · {z.comments(v.comments.length)}
                {v.files.length ? <span class="rv-meta"> · {v.files.join(', ')}</span> : null}
              </summary>
              <ul class="rv-prev-list">
                {v.comments.map((c) => (
                  <li key={c.id}>
                    <ZfLine c={c} lang={p.lang} />
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      ))}
    </section>
  );
}

/* ---------------- My work: reviews waiting for me ---------------- */

const W = texts(
  {
    title: 'Reviews waiting for you',
    stage: { internal: 'approve as CD', producer: 'send to the client', client: '', closed: '' } as Record<Stage, string>,
    version: (n: number) => `version ${n}`
  },
  {
    title: 'Granskningar som väntar på dig',
    stage: { internal: 'godkänn som CD', producer: 'skicka till kunden', client: '', closed: '' } as Record<Stage, string>,
    version: (n: number) => `version ${n}`
  }
);

export function ReviewsWaiting(p: { api: Api }) {
  const lang = useLang();
  const w = W[lang];
  const [rows, setRows] = useState<{ projectId: string; projectName: string; videoId: string; videoName: string; versionId: string; number: number; stage: Stage }[]>([]);
  useEffect(() => {
    void p.api.get<{ reviews: typeof rows }>('/api/review/waiting').then(
      (r) => setRows(r.reviews ?? []),
      () => setRows([])
    );
  }, []);
  if (!rows.length) return null;
  return (
    <section class="panel rv-waiting" aria-labelledby="rv-wait-h">
      <h2 class="blk-title" id="rv-wait-h">
        {w.title} <span class="fold-n">{rows.length}</span>
      </h2>
      <ul class="rv-wlist">
        {rows.map((r) => {
          const href = `/projects/${r.projectId}/review/${r.videoId}?version=${r.versionId}`;
          return (
            <li key={r.versionId}>
              <a href={href} onClick={(e) => (e.preventDefault(), navigate(href))}>
                <strong>{r.videoName}</strong>
              </a>{' '}
              <span class="rv-meta">
                {r.projectName} · {w.version(r.number)} · {w.stage[r.stage]}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ---------------- the Premiere panel's key ---------------- */

export const PANEL_TEXT = texts(
  {
    menu: 'Connect Premiere',
    title: 'Connect the Premiere panel',
    info: 'The Hub panel in Premiere Pro shows your review comments and puts them in the timeline as markers. Paste this key in the panel. It works until you make a new one, so keep it to yourself.',
    make: 'Make a key',
    again: 'Make a new key (the old one stops working)',
    key: 'Your key',
    api: 'Server address (under Advanced in the panel)',
    copy: 'Copy',
    copied: 'Copied',
    close: 'Close',
    install: 'How to install the panel: see Getting started in the account menu.'
  },
  {
    menu: 'Koppla Premiere',
    title: 'Koppla Premiere-panelen',
    info: 'Hubbens panel i Premiere Pro visar dina granskningskommentarer och lägger in dem som markörer i tidslinjen. Klistra in nyckeln i panelen. Den fungerar tills du gör en ny, så behåll den för dig själv.',
    make: 'Gör en nyckel',
    again: 'Gör en ny nyckel (den gamla slutar fungera)',
    key: 'Din nyckel',
    api: 'Hubbens adress (under Avancerat i panelen)',
    copy: 'Kopiera',
    copied: 'Kopierat',
    close: 'Stäng',
    install: 'Så installerar du panelen: se Kom igång i kontomenyn.'
  }
);

/** `path`: where the key is made – the account menu for staff, the personal link for freelancers. */
export function PanelKeyDialog(p: { api: Api; path?: string; onClose: () => void }) {
  const lang = useLang();
  const t = PANEL_TEXT[lang];
  const [key, setKey] = useState<{ token: string; apiUrl: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try {
      setKey(await p.api.post<{ token: string; apiUrl: string }>(p.path ?? '/api/me/panel-token', {}));
      // ticks the panel step in "Getting started" where the Hub has it; nothing to do where it hasn't
      p.api.put('/api/me/onboarding/panel', {}).catch(() => undefined);
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.title}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.close}
          </button>
          <button type="button" class="btn btn-primary" disabled={busy} onClick={() => void make()}>
            {key ? t.again : t.make}
          </button>
        </>
      }
    >
      <p>{t.info}</p>
      {key ? (
        <>
          <label class="field">
            <span>{t.key}</span>
            <span class="rv-row">
              <input class="input input--wide" readOnly value={key.token} onFocus={(e) => (e.currentTarget as HTMLInputElement).select()} />
              <button type="button" class="btn btn-sm" onClick={() => void copy(key.token, t.copied)}>
                {t.copy}
              </button>
            </span>
          </label>
          <label class="field">
            <span>{t.api}</span>
            <input class="input input--wide" readOnly value={key.apiUrl} onFocus={(e) => (e.currentTarget as HTMLInputElement).select()} />
          </label>
        </>
      ) : null}
      <p class="small muted">{t.install}</p>
    </Dialog>
  );
}

/* ---------------- the client's page in the portal ---------------- */

type Verdict = 'approved' | 'approved_minor' | 'changes';
interface PortalData {
  lang: Lang;
  project: { code: string; name: string };
  video: { code: string; name: string };
  versions: { id: string; round: number | null; phase: 'draft' | 'final'; waiting: boolean; outcome: string | null; sentAt: string | null }[];
  version: null | {
    id: string;
    round: number | null;
    phase: 'draft' | 'final';
    waiting: boolean;
    files: FileView[];
    comments: CommentView[];
    decision: { by: string; decision: Verdict; at: string } | null;
    approvers?: { name: string; you: boolean; decision: Verdict | null }[];
    yourDecision?: Verdict | null;
    yourTurn: boolean;
    canComment: boolean;
    decider: string | null;
    previous?: Previous | null;
    compare?: { id: string; round: number | null; files: FileView[] } | null;
  };
}

const C = texts(
  {
    back: 'Back to the project',
    round: (n: number) => `Round ${n}`,
    final: 'Final formats',
    approve: 'Approve',
    minor: 'Approve – with small adjustments',
    changes: 'Request changes',
    needComment: 'Add your comments in the film first – then ask for changes.',
    sure: { approved: 'Approve the film? We then finish it.', approved_minor: 'Approve with the small adjustments in your comments?', changes: 'Send your comments and ask for a new version?' },
    confirm: 'Yes, send',
    cancel: 'Not yet',
    decided: { approved: 'Approved', approved_minor: 'Approved with small adjustments', changes: 'Changes requested' } as Record<string, string>,
    by: (n: string) => `by ${n}`,
    thanks: 'Thank you! We have your answer.',
    deciderIs: (n: string) => `${n} approves this film. You can comment.`,
    notWaiting: 'This version is closed. Your comments are kept.',
    none: 'There is nothing to review here yet.',
    ready: 'Done reviewing?',
    approvers: 'Who approves',
    you: 'you',
    pending: 'not yet',
    compareRound: 'Compare with the previous round',
    noCompare: 'Hide the comparison',
    yourTurn: 'Waiting for you'
  },
  {
    back: 'Tillbaka till projektet',
    round: (n: number) => `Runda ${n}`,
    final: 'Slutformat',
    approve: 'Godkänn',
    minor: 'Godkänn – med små justeringar',
    changes: 'Begär ändringar',
    needComment: 'Skriv dina kommentarer i filmen först – begär sedan ändringar.',
    sure: { approved: 'Godkänna filmen? Då gör vi klart den.', approved_minor: 'Godkänna med de små justeringarna i dina kommentarer?', changes: 'Skicka dina kommentarer och be om en ny version?' },
    confirm: 'Ja, skicka',
    cancel: 'Inte än',
    decided: { approved: 'Godkänd', approved_minor: 'Godkänd med små justeringar', changes: 'Ändringar begärda' } as Record<string, string>,
    by: (n: string) => `av ${n}`,
    thanks: 'Tack! Vi har fått ditt svar.',
    deciderIs: (n: string) => `${n} godkänner den här filmen. Du kan kommentera.`,
    notWaiting: 'Den här versionen är stängd. Dina kommentarer finns kvar.',
    none: 'Här finns inget att granska ännu.',
    ready: 'Klar med granskningen?',
    approvers: 'Vem godkänner',
    you: 'du',
    pending: 'inte än',
    compareRound: 'Jämför med förra rundan',
    noCompare: 'Dölj jämförelsen',
    yourTurn: 'Väntar på dig'
  }
);

export function PortalReviewPage(p: { api: Api; code: string; lang: Lang | null; onLoaded?: (lang: Lang, title: string) => void }) {
  const theme = useReviewTheme();
  const [data, setData] = useState<PortalData | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [version, setVersion] = useState<string | null>(null);
  const [compare, setCompare] = useState<string | null>(null);
  const [ask, setAsk] = useState<Verdict | null>(null);
  const [busy, setBusy] = useState(false);
  const q = useMemo(() => new URLSearchParams({ ...(p.lang ? { lang: p.lang } : {}), ...(version ? { version } : {}), ...(compare ? { compare } : {}) }).toString(), [p.lang, version, compare]);
  const load = () =>
    p.api.get<PortalData>(`/api/portal/review/${encodeURIComponent(p.code)}${q ? `?${q}` : ''}`).then(
      (d) => (setData(d), setErr(null), p.onLoaded?.(d.lang, d.video.name)),
      (e) => setErr(e)
    );
  useEffect(() => {
    void load();
  }, [p.code, q]);
  if (err) return <ErrorBox error={err} onRetry={() => void load()} />;
  if (!data) return <Loading />;
  const c = C[data.lang];
  const v = data.version;
  const lq = p.lang ? `?lang=${p.lang}` : '';
  const send = async () => {
    if (!v || !ask) return;
    setBusy(true);
    try {
      await p.api.post(`/api/portal/review/${v.id}/decide${lq}`, { decision: ask });
      setAsk(null);
      toast({ text: c.thanks });
      await load();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  const hasMine = Boolean(v?.comments.some((x) => x.mine));
  const label = (x: { phase: 'draft' | 'final'; round: number | null }) => (x.phase === 'final' ? c.final : c.round(x.round ?? 1));
  const decided = v?.decision ? { d: v.decision.decision, by: v.decision.by } : v?.yourDecision ? { d: v.yourDecision, by: c.you } : null;
  // the client's one card above the comments (design B): who approves, then the decision
  const next = !v ? null : (
    <section class={`rv-decide rv-card rv-next${v.yourTurn ? ' is-yours rv-turn' : ''}`} data-tour="decide" role={v.yourTurn && ask ? 'alertdialog' : undefined} aria-label={v.yourTurn && ask ? c.sure[ask] : c.ready}>
      {decided ? (
        <p class="rv-decided">
          <Pill tone={decided.d === 'changes' ? 'warn' : 'ok'}>{c.decided[decided.d]}</Pill> {c.by(decided.by)}
        </p>
      ) : !v.waiting ? (
        <p class="rv-meta">{c.notWaiting}</p>
      ) : v.decider ? (
        <p class="rv-meta">{c.deciderIs(v.decider)}</p>
      ) : null}
      {v.yourTurn ? (
        ask ? (
          <>
            <p class="rv-next-h">{c.sure[ask]}</p>
            <div class="rv-row">
              <button type="button" class="btn btn-primary" disabled={busy} onClick={() => void send()}>
                {c.confirm}
              </button>
              <button type="button" class="btn" disabled={busy} onClick={() => setAsk(null)}>
                {c.cancel}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 class="rv-kicker" id="rv-ready">
              {c.ready}
            </h2>
            <div class="rv-stack">
              <button type="button" class="btn btn-primary" onClick={() => setAsk('approved')}>
                {c.approve}
              </button>
              <button type="button" class="btn" disabled={!hasMine} onClick={() => setAsk('approved_minor')}>
                {c.minor}
              </button>
              <button type="button" class="btn" disabled={!hasMine} onClick={() => setAsk('changes')}>
                {c.changes}
              </button>
            </div>
            {!hasMine ? <p class="rv-meta">{c.needComment}</p> : null}
          </>
        )
      ) : null}
      {v.approvers && v.approvers.length > 1 ? (
        <div class="rv-decisions" aria-label={c.approvers}>
          <h3 class="rv-h3">{c.approvers}</h3>
          <ul class="rv-dlist">
            {v.approvers.map((a, i) => (
              <li key={i}>
                <strong>{a.name}</strong>
                {a.you ? ` (${c.you})` : ''} · {a.decision ? c.decided[a.decision] : c.pending}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
  return (
    <div class="rv-page rv-portal">
      <nav class="rv-crumbs">
        <a href={`/portal/p/${encodeURIComponent(data.project.code)}`} onClick={(e) => (e.preventDefault(), navigate(`/portal/p/${encodeURIComponent(data.project.code)}`))}>
          ‹ {data.project.name}
        </a>
        {/* the review's guided tour again (the client has no Klara) */}
        <button type="button" class="linkish rv-howto" onClick={() => playTour()}>
          {TOUR_TEXT[data.lang].howReview}
        </button>
      </nav>
      {/* the first time a client opens a review: three steps, remembered in this browser */}
      <PageTour id="portal-review" lang={data.lang} />
      <header class="rv-head">
        <div class="rv-title">
          <h1 class="portal-h1">{data.video.name}</h1>
          {v?.yourTurn ? <Pill tone="warn">{c.yourTurn}</Pill> : null}
        </div>
        <span class="rv-row rv-tools">
          {data.versions.length > 1 && data.versions.length <= 5 ? (
            <span class="rv-seg rv-vtabs" role="group" aria-label={c.round(v?.round ?? 1)}>
              {data.versions.map((x) => (
                <button key={x.id} type="button" aria-pressed={x.id === v?.id} title={x.outcome ? c.decided[x.outcome] : undefined} onClick={() => x.id !== v?.id && (setVersion(x.id), setCompare(null))}>
                  {label(x)}
                </button>
              ))}
            </span>
          ) : data.versions.length > 1 ? (
            <select class="input rv-versions" value={v?.id ?? ''} aria-label={c.round(v?.round ?? 1)} onChange={(e) => (setVersion((e.currentTarget as HTMLSelectElement).value), setCompare(null))}>
              {data.versions.map((x) => (
                <option key={x.id} value={x.id}>
                  {label(x)}
                  {x.outcome ? ` · ${c.decided[x.outcome] ?? ''}` : ''}
                </option>
              ))}
            </select>
          ) : null}
          {v?.previous && data.versions.length > 1 ? (
            <button type="button" class="btn btn-sm" aria-pressed={Boolean(compare)} onClick={() => setCompare(compare ? null : v.previous!.id)}>
              {compare ? c.noCompare : c.compareRound}
            </button>
          ) : null}
          <ThemeSwitch lang={data.lang} theme={theme} />
        </span>
      </header>
      {!v ? (
        <p>{c.none}</p>
      ) : (
        <ReviewPlayer
          key={`${v.id}:${v.compare?.id ?? ''}`}
          lang={data.lang}
          files={v.files}
          comments={v.comments}
          staff={false}
          canComment={v.canComment}
          previous={v.previous}
          compare={v.compare ? { label: label({ phase: v.phase, round: v.compare.round }), files: v.compare.files } : null}
          thisLabel={label(v)}
          next={next}
          add={async (x) => {
            await p.api.post(`/api/portal/review/${v.id}/comments${lq}`, x);
            await load();
          }}
          remove={async (id) => {
            await p.api.put(`/api/portal/review/comments/${id}${lq}`, { delete: true });
            await load();
          }}
        />
      )}
    </div>
  );
}
